import mixpanel, { Config, Dict } from "mixpanel-browser";

/**
 * Centralized Mixpanel analytics service.
 *
 * Wraps the full mixpanel-browser feature set behind a single, typed API:
 * events, super properties, user profiles (people), group analytics,
 * GDPR opt-in/out, session replay, and auto link/form tracking.
 *
 * Safety: if VITE_MIXPANEL_TOKEN is not configured (e.g. non-dev envs),
 * every method becomes a no-op instead of throwing, so app code can call
 * analytics freely without guarding each site.
 */

const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN;

/** Whether Mixpanel is configured and active for this environment. */
export const isMixpanelEnabled = Boolean(MIXPANEL_TOKEN);

if (isMixpanelEnabled) {
  mixpanel.init(MIXPANEL_TOKEN, {
    debug: isMixpanelEnabled,
    // Automatically capture page views (SPA route changes tracked via trackPageView()).

    //capturing session with the rate 100%
    record_sessions_percent: 100,

    record_mask_all_text: false,
    record_mask_all_inputs: false,

    track_pageview: true,
    persistence: "localStorage",
    // Respect Do Not Track browser setting; flip to true to always track.
    ignore_dnt: false,
    // Batch network requests for fewer, more efficient calls.
    batch_requests: true,
  });
}

/** Run a Mixpanel call only when enabled; swallow errors so analytics never breaks the app. */
function safe<T>(fn: () => T): T | undefined {
  if (!isMixpanelEnabled) return undefined;
  try {
    return fn();
  } catch (error) {
    // if (isMixpanelEnabled) {

    // }
    return undefined;
  }
}

/** Shape of a user coming from the app (login response / profile API). */
export interface MixpanelUserProfile {
  /** Stable unique id used as the Mixpanel distinct_id. */
  id?: string | number;
  email?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  avatar?: string;
  role?: string;
  roleType?: string;
  baseRole?: string;
  /** Tenant / realm derived from hostname. */
  tenant?: string;
  /** Any extra custom properties to attach to the profile. */
  [key: string]: unknown;
}

/** Map an app user profile onto Mixpanel's reserved ($) people properties + custom ones. */
function toPeopleProps(user: MixpanelUserProfile): Dict {
  const props: Dict = {};
  if (user.email !== undefined) props.$email = user.email;
  if (user.name !== undefined) props.$name = user.name;
  if (user.firstName !== undefined) props.$first_name = user.firstName;
  if (user.lastName !== undefined) props.$last_name = user.lastName;
  if (user.phone !== undefined) props.$phone = user.phone;
  if (user.avatar !== undefined) props.$avatar = user.avatar;
  if (user.role !== undefined) props.role = user.role;
  if (user.roleType !== undefined) props.roleType = user.roleType;
  if (user.baseRole !== undefined) props.baseRole = user.baseRole;
  if (user.tenant !== undefined) props.tenant = user.tenant;

  // Copy any additional custom fields (skip the ones already mapped + id).
  const reserved = new Set([
    "id",
    "email",
    "name",
    "firstName",
    "lastName",
    "phone",
    "avatar",
    "role",
    "roleType",
    "baseRole",
    "tenant",
  ]);
  Object.keys(user).forEach((key) => {
    if (!reserved.has(key) && user[key] !== undefined) {
      props[key] = user[key];
    }
  });
  return props;
}

export const analytics = {
  /** The raw mixpanel instance for any advanced/edge-case usage. */
  instance: mixpanel,
  enabled: isMixpanelEnabled,

  // ─── Events ────────────────────────────────────────────────────────────
  /** Track a named event with optional properties. */
  track(event: string, properties?: Dict): void {
    safe(() => mixpanel.track(event, properties));
  },

  /** Track a page view. Call on SPA route changes. */
  trackPageView(properties?: Dict): void {
    safe(() => mixpanel.track_pageview(properties));
  },

  /**
   * Start a timer for an event. The next track() of the same event name
   * gets a `$duration` property (seconds). Call at the start of a flow.
   */
  timeEvent(event: string): void {
    safe(() => mixpanel.time_event(event));
  },

  /** Track an event associated with one or more groups. */
  trackWithGroups(event: string, properties: Dict, groups: Dict): void {
    safe(() => mixpanel.track_with_groups(event, properties, groups));
  },

  /** Auto-track clicks on elements matching a CSS selector. */
  trackLinks(selector: string, event: string, properties?: Dict): void {
    safe(() => mixpanel.track_links(selector, event, properties));
  },

  /** Auto-track submits on forms matching a CSS selector. */
  trackForms(selector: string, event: string, properties?: Dict): void {
    safe(() => mixpanel.track_forms(selector, event, properties));
  },

  // ─── Super properties (sent with every event) ──────────────────────────
  /** Register super properties persisted and attached to all future events. */
  register(properties: Dict, daysOrOptions?: number): void {
    safe(() => mixpanel.register(properties, daysOrOptions));
  },

  /** Register super properties only if not already set. */
  registerOnce(
    properties: Dict,
    defaultValue?: unknown,
    daysOrOptions?: number
  ): void {
    safe(() => mixpanel.register_once(properties, defaultValue, daysOrOptions));
  },

  /** Remove a single super property. */
  unregister(property: string): void {
    safe(() => mixpanel.unregister(property));
  },

  /** Read a current super property value. */
  getProperty(property: string): unknown {
    return safe(() => mixpanel.get_property(property));
  },

  // ─── Identity ──────────────────────────────────────────────────────────
  /** Associate all future events with a unique id (usually the user id). */
  identify(distinctId: string): void {
    safe(() => mixpanel.identify(distinctId));
  },

  /** The current distinct id Mixpanel is using. */
  getDistinctId(): string | undefined {
    return safe(() => mixpanel.get_distinct_id());
  },

  /** Create an alias that links a new id to the current distinct id (use once, at signup). */
  alias(alias: string, original?: string): void {
    safe(() => mixpanel.alias(alias, original));
  },

  /**
   * Identify a user and set/merge their profile in one call.
   * Call after login and whenever profile data refreshes.
   */
  identifyUser(user: MixpanelUserProfile): void {
    safe(() => {
      const distinctId = user.id !== null ? String(user.id) : user.email;
      if (distinctId) mixpanel.identify(distinctId);
      const props = toPeopleProps(user);
      mixpanel.people.set(props);
      // Also expose common fields as super props for event-level filtering.
      const superProps: Dict = {};
      if (user.role !== undefined) superProps.role = user.role;
      if (user.roleType !== undefined) superProps.roleType = user.roleType;
      if (user.tenant !== undefined) superProps.tenant = user.tenant;
      if (Object.keys(superProps).length) mixpanel.register(superProps);
    });
  },

  /** Clear all identity & super properties. Call on logout. */
  reset(): void {
    safe(() => mixpanel.reset());
  },

  // ─── People / user profile ─────────────────────────────────────────────
  people: {
    /** Set profile properties (overwrites existing). */
    set(properties: Dict): void {
      safe(() => mixpanel.people.set(properties));
    },
    /** Set profile properties only if not already set. */
    setOnce(properties: Dict): void {
      safe(() => mixpanel.people.set_once(properties));
    },
    /** Increment numeric profile properties. */
    increment(properties: string | Dict, by?: number): void {
      safe(() =>
        typeof properties === "string" && by !== undefined
          ? mixpanel.people.increment(properties, by)
          : mixpanel.people.increment(properties as Dict)
      );
    },
    /** Append value(s) to list properties. */
    append(properties: Dict): void {
      safe(() => mixpanel.people.append(properties));
    },
    /** Add value(s) to list properties, keeping them unique (set union). */
    union(properties: Dict): void {
      safe(() => mixpanel.people.union(properties));
    },
    /** Remove a value from a list property. */
    remove(properties: Dict): void {
      safe(() => mixpanel.people.remove(properties));
    },
    /** Permanently delete named profile properties. */
    unset(properties: string | string[]): void {
      safe(() => mixpanel.people.unset(properties));
    },
    /** Record a revenue/charge event on the profile. */
    trackCharge(amount: number, properties?: Dict): void {
      safe(() => mixpanel.people.track_charge(amount, properties));
    },
    /** Clear the profile's charge history. */
    clearCharges(): void {
      safe(() => mixpanel.people.clear_charges());
    },
    /** Permanently delete the user profile. */
    deleteUser(): void {
      safe(() => mixpanel.people.delete_user());
    },
  },

  // ─── Group analytics ───────────────────────────────────────────────────
  groups: {
    /** Set the group(s) the current user belongs to for a group key. */
    set(groupKey: string, groupIds: string | string[]): void {
      safe(() => mixpanel.set_group(groupKey, groupIds));
    },
    /** Add the user to a group. */
    add(groupKey: string, groupId: string): void {
      safe(() => mixpanel.add_group(groupKey, groupId));
    },
    /** Remove the user from a group. */
    remove(groupKey: string, groupId: string): void {
      safe(() => mixpanel.remove_group(groupKey, groupId));
    },
    /** Set properties on a group's profile. */
    setProperties(groupKey: string, groupId: string, properties: Dict): void {
      safe(() => mixpanel.get_group(groupKey, groupId).set(properties));
    },
    /** Set group properties only if not already set. */
    setPropertiesOnce(
      groupKey: string,
      groupId: string,
      properties: Dict
    ): void {
      safe(() => mixpanel.get_group(groupKey, groupId).set_once(properties));
    },
    /** Remove named properties from a group. */
    unsetProperties(
      groupKey: string,
      groupId: string,
      properties: string
    ): void {
      safe(() => mixpanel.get_group(groupKey, groupId).unset(properties));
    },
  },

  // ─── GDPR / consent ────────────────────────────────────────────────────
  consent: {
    /** Opt this user into tracking. */
    optIn(): void {
      safe(() => mixpanel.opt_in_tracking());
    },
    /** Opt this user out of tracking (also stops persistence). */
    optOut(): void {
      safe(() => mixpanel.opt_out_tracking());
    },
    /** Whether the user has opted out. */
    hasOptedOut(): boolean {
      return safe(() => mixpanel.has_opted_out_tracking()) ?? false;
    },
    /** Whether the user has explicitly opted in. */
    hasOptedIn(): boolean {
      return safe(() => mixpanel.has_opted_in_tracking()) ?? false;
    },
    /** Clear the stored opt-in/out decision. */
    clear(): void {
      safe(() => mixpanel.clear_opt_in_out_tracking());
    },
  },

  // ─── Session replay ────────────────────────────────────────────────────
  sessionRecording: {
    start(): void {
      safe(() => mixpanel.start_session_recording());
    },
    stop(): void {
      safe(() => mixpanel.stop_session_recording());
    },
    /** Get the current replay id (to attach to events/support tickets). */
    getProperties(): Record<string, unknown> {
      return safe(() => mixpanel.get_session_recording_properties()) ?? {};
    },
  },

  // ─── Config / misc ─────────────────────────────────────────────────────
  /** Update Mixpanel config at runtime. */
  setConfig(config: Partial<Config>): void {
    safe(() => mixpanel.set_config(config));
  },
};

/** Convenience named exports for the two most common call sites. */
export const identifyUser = analytics.identifyUser;
export const resetUser = analytics.reset;

export default mixpanel;
